import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { FlightFilters } from '@/lib/api/flights';
import { useFlights } from '@/lib/api/queries';
import { useMemo } from 'react';

export type FlightsIdMode = 'callsign' | 'serial';
export type FlightsSourceFilter = 'all' | 'historical' | 'planned';

/** Time windows the toolbar offers, as days back from now. `null` = all time.
 *
 *  This is the one filter that materially changes the load: an unfiltered
 *  Flights context pages the tenant's ENTIRE history (439 records / ~858 KB
 *  measured 2026-08-26) because the API still has no `aircraft_serial` filter.
 *  Narrowing the window is currently the only way to cut that down. */
export type FlightsWindow = 7 | 30 | 90 | null;

interface FlightsStore {
  selectedSerial: string | null;
  idMode: FlightsIdMode;
  showOnline: boolean;
  showOffline: boolean;
  sourceFilter: FlightsSourceFilter;
  openFlightId: string | null;

  // ── Server-side filters (spec §2.1, added 2026-08-27) ──
  // Distinct from `sourceFilter` above, which is a CLIENT-side split on the
  // `planned` flag. These are sent as query params and narrow what the API
  // returns at all. Every one defaults to "no filter", so the context behaves
  // exactly as it did before until the user picks something.
  windowDays: FlightsWindow;
  /** `utm_state`, exact match — 'active' | 'ended' | 'cancelled' | 'rejected' | 'held' | 'end_failed'. */
  utmState: string | null;
  /** `source`, exact match on telemetry origin — 'DJI Flight Hub' etc. */
  connector: string | null;

  selectSerial: (serial: string | null) => void;
  setIdMode: (m: FlightsIdMode) => void;
  toggleState: (k: 'online' | 'offline') => void;
  setSourceFilter: (v: FlightsSourceFilter) => void;
  openFlight: (id: string | null) => void;
  setWindowDays: (d: FlightsWindow) => void;
  setUtmState: (s: string | null) => void;
  setConnector: (s: string | null) => void;
}

/** Session-only — deliberately NOT persisted. A stored `openFlightId` would
 *  reopen a detail view for a flight that may no longer be in the list. */
export const useFlightsStore = create<FlightsStore>()(
  devtools(
    (set) => ({
      selectedSerial: null,
      idMode: 'callsign',
      showOnline: true,
      showOffline: true,
      sourceFilter: 'all',
      openFlightId: null,
      windowDays: null,
      utmState: null,
      connector: null,

      // Changing aircraft closes any open detail view — the mockup's
      // selectFlightsAircraft calls closeFlightDetail() first.
      selectSerial: (serial) => set({ selectedSerial: serial, openFlightId: null }),
      setIdMode: (idMode) => set({ idMode }),
      toggleState: (k) =>
        set((s) =>
          k === 'online' ? { showOnline: !s.showOnline } : { showOffline: !s.showOffline },
        ),
      setSourceFilter: (sourceFilter) => set({ sourceFilter }),
      openFlight: (openFlightId) => set({ openFlightId }),
      // Changing a server-side filter refetches, so the open detail view may
      // be for a flight that drops out of the new result. Close it, same
      // reasoning as selectSerial.
      setWindowDays: (windowDays) => set({ windowDays, openFlightId: null }),
      setUtmState: (utmState) => set({ utmState, openFlightId: null }),
      setConnector: (connector) => set({ connector, openFlightId: null }),
    }),
    { name: 'flights' },
  ),
);

/**
 * Project the store's server-side filter state onto the API's query params.
 *
 * `from` is computed at call time rather than stored, so the window stays
 * relative to now instead of freezing at whenever the user picked it. It is
 * quantised to the start of the UTC day: an exact `now - 30d` timestamp would
 * differ on every render and thrash the query cache, since the filter set is
 * part of the query key.
 *
 * No `to` — an open-ended upper bound is what "last N days" means, and
 * pinning one would exclude a flight created a second from now.
 */
export function flightsFiltersFrom(s: {
  windowDays: FlightsWindow;
  utmState: string | null;
  connector: string | null;
}): FlightFilters {
  const filters: FlightFilters = {};
  if (s.windowDays != null) {
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - s.windowDays);
    start.setUTCHours(0, 0, 0, 0);
    filters.from = start.toISOString();
  }
  if (s.utmState) filters.utm_state = s.utmState;
  if (s.connector) filters.source = s.connector;
  return filters;
}

/**
 * The Flights query, filtered by the store's server-side filter state.
 *
 * EVERY consumer must go through this rather than calling `useFlights()`
 * directly. The filter set is part of the query key, so a call site that
 * skipped it would open a second cache entry and page the tenant's whole
 * history again alongside the filtered one — the exact cost the filters
 * exist to avoid.
 *
 * `enabled` is passed through for NavRail, which must not trigger the fetch
 * in live mode (see `useReservedFlights`).
 */
export function useFilteredFlights(opts?: { enabled?: boolean }) {
  const windowDays = useFlightsStore((s) => s.windowDays);
  const utmState = useFlightsStore((s) => s.utmState);
  const connector = useFlightsStore((s) => s.connector);
  const filters = useMemo(
    () => flightsFiltersFrom({ windowDays, utmState, connector }),
    [windowDays, utmState, connector],
  );
  return useFlights({ filters, ...(opts?.enabled != null && { enabled: opts.enabled }) });
}
